var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { useLayer } from 'react-laag';
import { NotificationCard } from '../../elements/notification-card/NotificationCard';
import { CardContainer, TooltipContent, TooltipTrigger } from './Tooltip.styling';
import { isReactText, useTooltipTrigger } from './helpers';
/**
 * A tooltip component that displays content when the user hovers over a trigger element.
 *
 * ## Usage
 *
 * The `<Tooltip>` component must have exactly 2 children:
 * - One `<Tooltip.Content>` element that contains the content to be displayed in the tooltip.
 * - One trigger element that the user will hover over to display the tooltip.
 *
 * ### Component trigger
 *
 * You can use any component as a trigger for the tooltip. For example, a Tarasa `<Btn>`.
 * The button will be rendered just like any other button. When the user hovers over the button,
 * a tooltip will appear with the content 'Tooltip title' and 'Tooltip content'.
 *
 * > NOTE: No additional styling is added to the trigger.
 *
 * @example
 * ```jsx
 * <Tooltip>
 *   <Tooltip.Content>
 *     <h1>Tooltip title</h1>
 *     <p>Tooltip content</p>
 *   </Tooltip.Content>
 *   <Btn>Hover over me</Btn>
 * </Tooltip>
 * ```
 *
 * ### Text trigger
 *
 * You can also use some straightforward text as the trigger. This is useful, for example, when
 * a part of a larger text needs to show extra description on hover (ie. think abbreviations).
 * This will render a text 'Hover over me'. When the user hovers over the text,
 * a tooltip will appear with the content 'Tooltip title' and 'Tooltip content'.
 *
 * > NOTE: No additional styling is added to the trigger.
 *
 * @example
 * ```jsx
 * <Tooltip>
 *   <Tooltip.Content>...</Tooltip.Content>
 *   Hover over me
 * </Tooltip>
 * ```
 *
 * ### Tooltip trigger
 *
 * You can also use the `<Tooltip.Trigger>` component to wrap the trigger element. This automatically
 * makes the cursor a pointer and changes the color of the text when the user hovers over it.
 * This example will render an icon. When the user hovers over the icon, the cursor will change to a pointer
 * and the color of the icon will change to the primary color. And, of course, the tooltip will appear.
 *
 * @example
 * ```jsx
 * <Tooltip>
 *   <Tooltip.Content>...</Tooltip.Content>
 *   <Tooltip.Trigger>
 *     <Icon name="QuestionMark" />
 *   </Tooltip.Trigger>
 * </Tooltip>
 * ```
 *
 * ### Tooltip placement
 *
 * You can optionally specify the placement of the tooltip. The default placement is 'top-center'.
 * If there is not enough space for the tooltip in the specified placement, it will be automatically
 * adjusted to fit within the viewport.
 *
 * @example
 * ```jsx
 * <Tooltip placement="bottom-center">
 *  ...
 * </Tooltip>
 * ```
 *
 * ### Tooltip delay
 *
 * You can optionally specify the delay in milliseconds before the tooltip appears and disappears.
 * By default, the tooltip will appear immediately when the user hovers over the trigger element.
 * The tooltip will disappear after 200 milliseconds. This allows the user to move the cursor from
 * the trigger to the tooltip content, because there is a slight gap. If it is not necessary for the
 * user to be able to move the cursor from the trigger to the tooltip content, you can set the delay
 * to 0. This will make the tooltip disappear immediately when the cursor leaves the trigger.
 *
 * @example
 * ```jsx
 * <Tooltip delayEnter={500} delayLeave={1000}>
 *   ...
 * </Tooltip>
 * ```
 */
export var Tooltip = function (_a) {
    var children = _a.children, placement = _a.placement, delayEnter = _a.delayEnter, delayLeave = _a.delayLeave, color = _a.color;
    var _b = useTooltipTrigger({ delayEnter: delayEnter, delayLeave: delayLeave }), isOpen = _b.isOpen, internalTriggerProps = _b.triggerProps, internalContentProps = _b.contentProps;
    var _c = useLayer({
        auto: true,
        isOpen: isOpen,
        placement: placement || 'top-center',
    }), triggerProps = _c.triggerProps, contentProps = _c.layerProps, renderLayer = _c.renderLayer;
    if (!children || !Array.isArray(children) || children.length !== 2) {
        throw new Error('The <Tooltip> component must have exactly 2 children - one <Tooltip.Content> and one trigger component.');
    }
    var triggerChild = children === null || children === void 0 ? void 0 : children.find(function (child) { return child.type !== TooltipContent; });
    if (!triggerChild) {
        throw new Error('The <Tooltip> component must have a single trigger element.');
    }
    var contentChild = children === null || children === void 0 ? void 0 : children.find(function (child) { return child.type === TooltipContent; });
    if (!contentChild) {
        throw new Error('The <Tooltip> component must have a single <Tooltip.Content> element.');
    }
    /**
     * Prepare the trigger element to be rendered. There are two cases:
     *
     * ## Case 1
     * The trigger element is raw text or a functional component. In this case, we need to wrap
     * the trigger element in an HTML element to attach the trigger props and for the ref needed
     * for react-laag to work properly.
     *
     * ```jsx
     * <Tooltip>
     *   <Tooltip.Content>...</Tooltip.Content>
     *   Hover over me
     * </Tooltip>
     *
     * // or
     *
     * const FunctionalComponent = () => <div>Hover over me</div>;
     *
     * <Tooltip>
     *   <Tooltip.Content>...</Tooltip.Content>
     *   <FunctionalComponent />
     * </Tooltip>
     * ```
     *
     * ## Case 2
     * The trigger element is a React component, for example an HTML element or a styled component.
     * In this case, we need to clone the element and inject the trigger props.
     *
     * ```jsx
     * <Tooltip>
     *   <Tooltip.Content>...</Tooltip.Content>
     *   <button>Hover over me</button>
     * </Tooltip>
     *
     * // or
     *
     * const Btn = styled.button``;
     *
     * <Tooltip>
     *   <Tooltip.Content>...</Tooltip.Content>
     *   <Btn>Hover over me</Btn>
     * </Tooltip>
     * ```
     */
    var trigger = isReactText(triggerChild) || typeof triggerChild.type === 'function' ? (React.createElement("span", __assign({}, triggerProps, internalTriggerProps), triggerChild)) : (React.cloneElement(triggerChild, __assign(__assign({}, triggerProps), internalTriggerProps)));
    return (React.createElement(React.Fragment, null,
        trigger,
        renderLayer(React.createElement(React.Fragment, null, isOpen && (React.createElement(CardContainer, __assign({}, contentProps, internalContentProps),
            React.createElement(NotificationCard, { color: color }, contentChild)))))));
};
Tooltip.Content = TooltipContent;
Tooltip.Trigger = TooltipTrigger;
